import '../../pages/RankAthletesPage.css';

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AthleteCard from '../../components/common/AthleteCard/AthleteCard';
import AthleteSearchBar from '../../components/common/AthleteSearchBar/AthleteSearchBar';
import PageTopNav from '../../components/common/PageTopNav/PageTopNav';
import RankAthleteDetail from '../../components/common/RankAthleteDetail/RankAthleteDetail';
import ScrollToTopFab from '../../components/common/ScrollToTopFab/ScrollToTopFab';
import { ChevronRightIcon } from '../../pages/TryoutIcons';
import { AthletePosition, AthleteWithId } from '../../types/athlete';
import DemoVideoViewer from '../components/DemoVideoViewer';
import { useDemoStore } from '../store/DemoStoreContext';

type FilterValue = 'all' | AthletePosition;
type TeamFilterValue = 'all' | 'unassigned' | string;

/** Position counts for one team. `unlisted` holds athletes with no known position. */
interface TeamPositionCounts {
  byPosition: Record<string, number>;
  unlisted: number;
  total: number;
}

const SCROLL_TOP_FAB_THRESHOLD_PX = 180;

interface SummarySectionHeaderProps {
  title: string;
  open: boolean;
  onToggle: () => void;
}

/** Collapsible section header inside the summary card. */
const SummarySectionHeader: React.FC<SummarySectionHeaderProps> = ({
  title,
  open,
  onToggle,
}) => (
  <button
    type="button"
    className="rank-athletes-summary-header"
    onClick={onToggle}
    aria-expanded={open}
  >
    <span className="rank-athletes-summary-title">{title}</span>
    <span
      className={`rank-athletes-summary-arrow${open ? ' rank-athletes-summary-arrow--open' : ''}`}
    >
      <ChevronRightIcon size={18} />
    </span>
  </button>
);

const DemoRankAthletesPage: React.FC = () => {
  const navigate = useNavigate();
  const { tryoutMeta, athletes, setTeamPlacement } = useDemoStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState<FilterValue>('all');
  const [teamFilter, setTeamFilter] = useState<TeamFilterValue>('all');
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [selectedAthleteId, setSelectedAthleteId] = useState<string | null>(null);
  const [scrollTopFabVisible, setScrollTopFabVisible] = useState(false);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  const handleContentScroll = useCallback((): void => {
    const el = contentScrollRef.current;
    if (!el) return;
    setScrollTopFabVisible(el.scrollTop > SCROLL_TOP_FAB_THRESHOLD_PX);
  }, []);

  const scrollContentToTop = useCallback((): void => {
    contentScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const sortedAthletes = useMemo<AthleteWithId[]>(() => {
    return [...athletes].sort((a, b) => {
      const na = parseInt(a.number, 10);
      const nb = parseInt(b.number, 10);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.number.localeCompare(b.number);
    });
  }, [athletes]);

  const isSearching = searchQuery.trim().length > 0;

  const filteredAthletes = useMemo<AthleteWithId[]>(() => {
    let list = sortedAthletes;
    if (!isSearching) {
      if (positionFilter !== 'all') list = list.filter((a) => a.position === positionFilter);
      if (teamFilter === 'unassigned') list = list.filter((a) => !a.teamPlacement);
      else if (teamFilter !== 'all') list = list.filter((a) => a.teamPlacement === teamFilter);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (a) => a.name.toLowerCase().includes(q) || a.number.toLowerCase().includes(q),
      );
    }
    return list;
  }, [sortedAthletes, positionFilter, teamFilter, searchQuery, isSearching]);

  /** Tryout-configured positions, stable order, trimmed, no duplicates. */
  const tryoutPositions = useMemo<string[]>(() => {
    const seen = new Set<string>();
    const positions: string[] = [];
    for (const raw of tryoutMeta.athletePositions) {
      const p = raw.trim();
      if (p && !seen.has(p)) { seen.add(p); positions.push(p); }
    }
    return positions;
  }, [tryoutMeta.athletePositions]);

  const positionFilterOptions = useMemo<Array<{ label: string; value: FilterValue }>>(
    () => [{ label: 'All', value: 'all' }, ...tryoutPositions.map((p) => ({ label: p, value: p }))],
    [tryoutPositions],
  );

  const teamFilterOptions = useMemo<Array<{ label: string; value: TeamFilterValue }>>(() => {
    if (tryoutMeta.teams.length === 0) return [];
    return [
      { label: 'All', value: 'all' },
      ...tryoutMeta.teams.map((t) => ({ label: t, value: t })),
      { label: 'Unassigned', value: 'unassigned' },
    ];
  }, [tryoutMeta.teams]);

  const teamPlacementCounts = useMemo(() => {
    const counts: Record<string, number> = { unassigned: 0 };
    for (const team of tryoutMeta.teams) counts[team] = 0;
    for (const a of athletes) {
      if (a.teamPlacement && counts[a.teamPlacement] !== undefined) counts[a.teamPlacement]++;
      else counts.unassigned++;
    }
    return counts;
  }, [athletes, tryoutMeta.teams]);

  /** Per-team position counts. Every tryout position appears, zero included. */
  const teamPositionCounts = useMemo<Record<string, TeamPositionCounts>>(() => {
    const knownPositions = new Set(tryoutPositions);
    const counts: Record<string, TeamPositionCounts> = {};

    for (const team of tryoutMeta.teams) {
      const byPosition: Record<string, number> = {};
      for (const position of tryoutPositions) byPosition[position] = 0;
      counts[team] = { byPosition, unlisted: 0, total: 0 };
    }

    for (const a of athletes) {
      const row = a.teamPlacement ? counts[a.teamPlacement] : undefined;
      // Skip an unassigned athlete, or a placement on a team the tryout no longer lists.
      if (!row) continue;
      const position = a.position.trim();
      if (knownPositions.has(position)) row.byPosition[position]++;
      else row.unlisted++;
      row.total++;
    }

    return counts;
  }, [athletes, tryoutMeta.teams, tryoutPositions]);

  const selectedAthlete = useMemo(
    () => (selectedAthleteId ? (filteredAthletes.find((a) => a.id === selectedAthleteId) ?? null) : null),
    [selectedAthleteId, filteredAthletes],
  );
  const selectedAthleteIndex = useMemo(
    () => (selectedAthleteId ? filteredAthletes.findIndex((a) => a.id === selectedAthleteId) : -1),
    [selectedAthleteId, filteredAthletes],
  );

  const handlePrev = useCallback((): void => {
    if (selectedAthleteIndex <= 0) return;
    setSelectedAthleteId(filteredAthletes[selectedAthleteIndex - 1].id);
  }, [filteredAthletes, selectedAthleteIndex]);

  const handleNext = useCallback((): void => {
    if (selectedAthleteIndex < 0 || selectedAthleteIndex >= filteredAthletes.length - 1) return;
    setSelectedAthleteId(filteredAthletes[selectedAthleteIndex + 1].id);
  }, [filteredAthletes, selectedAthleteIndex]);

  const handleTeamPlacementChange = useCallback(
    (teamPlacement: string | null): void => {
      if (!selectedAthleteId) return;
      setTeamPlacement(selectedAthleteId, teamPlacement);
    },
    [selectedAthleteId, setTeamPlacement],
  );

  return (
    <div className="rank-athletes-shell">
      <PageTopNav
        onBack={() => navigate('/demo/tryout')}
        backAriaLabel="Back to tryout dashboard"
        title={`${tryoutMeta.name} Ranking`}
      />
      <div
        ref={contentScrollRef}
        onScroll={handleContentScroll}
        className="rank-athletes-content"
      >
        <AthleteSearchBar value={searchQuery} onChange={setSearchQuery} />

        {!isSearching && positionFilterOptions.length > 1 ? (
          <div className="rank-athletes-filter-row" role="group" aria-label="Filter by position">
            {positionFilterOptions.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                className={[
                  'rank-athletes-filter-chip',
                  positionFilter === value ? 'rank-athletes-filter-chip--active' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => setPositionFilter(value)}
                aria-pressed={positionFilter === value}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {!isSearching && teamFilterOptions.length > 0 ? (
          <div className="rank-athletes-filter-row" role="group" aria-label="Filter by team">
            {teamFilterOptions.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                className={[
                  'rank-athletes-filter-chip',
                  teamFilter === value ? 'rank-athletes-filter-chip--active' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => setTeamFilter(value)}
                aria-pressed={teamFilter === value}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {!isSearching && tryoutMeta.teams.length > 0 ? (
          <div className="rank-athletes-summary">
            <SummarySectionHeader
              title="Team Placement Summary"
              open={summaryOpen}
              onToggle={() => setSummaryOpen((o) => !o)}
            />
            {summaryOpen ? (
              <div className="rank-athletes-summary-counts">
                {tryoutMeta.teams.map((team) => (
                  <div key={team} className="rank-athletes-summary-count">
                    <span className="rank-athletes-summary-num">{teamPlacementCounts[team] ?? 0}</span>
                    <span className="rank-athletes-summary-label">{team}</span>
                  </div>
                ))}
                <div className="rank-athletes-summary-count rank-athletes-summary-count--unassigned">
                  <span className="rank-athletes-summary-num">{teamPlacementCounts.unassigned ?? 0}</span>
                  <span className="rank-athletes-summary-label">Unassigned</span>
                </div>
              </div>
            ) : null}

            {/* Position breakdown — one block per team */}
            {tryoutPositions.length > 0 ? (
              <>
                <SummarySectionHeader
                  title="Position Breakdown"
                  open={breakdownOpen}
                  onToggle={() => setBreakdownOpen((o) => !o)}
                />
                {breakdownOpen ? (
                  <div className="rank-athletes-breakdown">
                    {tryoutMeta.teams.map((team) => {
                      const counts = teamPositionCounts[team];
                      return (
                        <div key={team} className="rank-athletes-breakdown-team">
                          <div className="rank-athletes-breakdown-team-head">
                            <span className="rank-athletes-breakdown-team-name">{team}</span>
                            <span className="rank-athletes-breakdown-team-total">{counts?.total ?? 0}</span>
                          </div>
                          <div className="rank-athletes-breakdown-pills">
                            {tryoutPositions.map((position) => {
                              const count = counts?.byPosition[position] ?? 0;
                              return (
                                <span
                                  key={position}
                                  className={[
                                    'rank-athletes-breakdown-pill',
                                    count === 0 ? 'rank-athletes-breakdown-pill--zero' : '',
                                  ].filter(Boolean).join(' ')}
                                >
                                  <span className="rank-athletes-breakdown-pill-label">{position}</span>
                                  <span className="rank-athletes-breakdown-pill-num">{count}</span>
                                </span>
                              );
                            })}
                            {counts && counts.unlisted > 0 ? (
                              <span className="rank-athletes-breakdown-pill rank-athletes-breakdown-pill--unlisted">
                                <span className="rank-athletes-breakdown-pill-label">No position</span>
                                <span className="rank-athletes-breakdown-pill-num">{counts.unlisted}</span>
                              </span>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}

        {filteredAthletes.length === 0 ? (
          <div className="rank-athletes-empty">
            <p className="rank-athletes-empty-title">No athletes match your search.</p>
          </div>
        ) : (
          <ul className="rank-athletes-list" aria-label="Athletes">
            {filteredAthletes.map((athlete) => (
              <li key={athlete.id}>
                <AthleteCard
                  id={athlete.id}
                  name={athlete.name}
                  number={athlete.number}
                  position={athlete.position}
                  returningstatus={athlete.returningstatus}
                  imageThumbnailUrl={athlete.imageThumbnailUrl}
                  teamPlacement={athlete.teamPlacement ?? undefined}
                  onClick={(id) => setSelectedAthleteId(id)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      <ScrollToTopFab visible={scrollTopFabVisible} onPress={scrollContentToTop} />
      {selectedAthlete ? (
        <RankAthleteDetail
          athlete={selectedAthlete}
          formSchema={tryoutMeta.formSchema}
          teams={tryoutMeta.teams}
          onClose={() => setSelectedAthleteId(null)}
          onTeamPlacementChange={handleTeamPlacementChange}
          onPrevious={handlePrev}
          onNext={handleNext}
          hasPrevious={selectedAthleteIndex > 0}
          hasNext={selectedAthleteIndex < filteredAthletes.length - 1}
          renderVideo={(videoUid, label) => <DemoVideoViewer videoUid={videoUid} label={label} />}
        />
      ) : null}
    </div>
  );
};

export default DemoRankAthletesPage;
